import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAtom } from 'jotai';
import moment from 'moment';
import { tid } from '@utils/test';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { appliedIntervalAtom, appliedFilterAtom, enableDodAtom } from '../atoms';
import More from './More';
import { getAppDefaultGranularity } from '@utils/granularity';
import MemoDatePicker from './Datepickers';
import { SummaryContainer, TopWrapper, FilterContainer, Controls, DodToggleLabel } from './styled';
import DynamicFilterBuilder from '@src/components/filter/containers/DynamicFilterBuilder';
import { dataAccessDurationSelector } from '@src/store/common/selectors';
import { useStartObservingHeight } from '@src/atoms/page-styles';
import { DIMENSION_TYPES_CONSTANTS } from '@src/utils/metrics';
import { FIXED_APP_FILTERS } from '@src/components/filter-panel/constants';

function OverviewSummary() {
  const summaryDatePickerRef = useRef();
  const [appliedFilter, setAppliedFilter] = useAtom(appliedFilterAtom);
  const filterPanelRef = useRef(null);
  const allDimensions = useSelector<unknown, any>(allDimensionsListSelector);
  const [interval, setInterval] = useAtom(appliedIntervalAtom);
  const durationAllowed = useSelector(dataAccessDurationSelector);
  const [enableDod, setEnableDod] = useAtom(enableDodAtom);

  const globalDimensionsList = allDimensions.filter((dimension) => {
    return dimension.type === DIMENSION_TYPES_CONSTANTS.immutableGlobalMetadata;
  });

  const dimensionsLookUp = React.useMemo(() => {
    const dimensionObject: Record<string, string> = {};
    globalDimensionsList.forEach((dimension) => {
      dimensionObject[dimension.groupBy] = dimension.name;
    });
    return dimensionObject;
  }, [globalDimensionsList]);

  // Observe height of filter panel, will update the top distance of each fixed element
  useStartObservingHeight(filterPanelRef);

  // Give a negative number to preset filters'`fid`, so that they can be distinguished from saved filters
  const PRESET_FILTERS = FIXED_APP_FILTERS.map((it, ix) => ({ ...it, fid: -1000 - ix }));

  const onApplyFilter = (filterValue) => {
    let fid: number | undefined = filterValue.fid;
    if (
      filterValue.fid === undefined &&
      PRESET_FILTERS.some((it) => it.name === filterValue.name)
    ) {
      fid = PRESET_FILTERS.find((it) => it.name === filterValue.name)?.fid;
    }

    // Ensure we have the required properties and a new _rKey to trigger change detection
    const filter = {
      name: filterValue.name,
      rule: filterValue.rule,
      fid,
      _rKey: filterValue._rKey || Date.now(),
    };

    // Update the filter - persistence is handled automatically at the dashboard level
    setAppliedFilter(filter);
  };

  const hasIntervalChanged = (newInterval) => {
    try {
      if (
        newInterval[0].isSame(moment(interval.from)) &&
        newInterval[1].isSame(moment(interval.to))
      ) {
        return false;
      }
    } catch (err) {
      // pass
      // falls back to current behaviour
    }
    return true;
  };

  const validateUpdateInterval = (dates, shortcut) => {
    if (hasIntervalChanged(dates)) {
      const granularity = getAppDefaultGranularity({
        from: interval.from,
        to: interval.to,
        minuteGranularity: false,
        overview: false,
        shortcut: interval.shortcut,
        dashboardType: interval.dashboardType,
      });
      setInterval({
        from: dates[0].format(),
        to: dates[1].format(),
        shortcut,
        dashboardType: 'overview',
        granularity,
      });
    }
  };

  return (
    <>
      <SummaryContainer id="filter-panel-container" ref={filterPanelRef} {...tid('summary-panel')}>
        <TopWrapper {...tid('controls')}>
          <FilterContainer>
            <DynamicFilterBuilder
              onApplyFilter={onApplyFilter}
              appliedFilter={appliedFilter}
              dimensionsList={globalDimensionsList}
              dimensionsLookUp={dimensionsLookUp}
            />
          </FilterContainer>
          <span></span>
          <Controls>
            <DodToggleLabel>
              <input
                type="checkbox"
                checked={enableDod}
                onChange={(e) => setEnableDod(e.target.checked)}
              />
              Date Compare
            </DodToggleLabel>
            <MemoDatePicker
              key={`${JSON.stringify(interval)}`}
              interval={interval}
              validateUpdateInterval={validateUpdateInterval}
              summaryDatePickerRef={summaryDatePickerRef}
              durationAllowed={durationAllowed}
            />
          </Controls>
          <More />
        </TopWrapper>
      </SummaryContainer>
    </>
  );
}

export default OverviewSummary;
